import { useTranslator } from '@Localization';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { GTable, IGColumnProps } from '../../../../../../__legacy__/components/table/GTable';
import { toLocaleDateTimeString } from '../../../../../../__legacy__/helpers/date.helpers';
import { IPagingInfoWithOrder } from '../../../../../../__legacy__/models/paginationInfo.model';
import { S_TableClipLoader } from '../../../../../../design/loaders/clipLoader/S_TableClipLoader';
import { IProjectExport, ProjectExportStatus } from '../../models/ProjectExport';
import { ProjectExportDropdownContainer } from '../dropdown/ProjectExportDropdown.container';
import { ExportStatusBadge } from '../statusBadge/ExportStatusBadge';

interface IProps {
  isLoading: boolean;
  items: IProjectExport[];
  paging: IPagingInfoWithOrder;
  onOrderingChange: (orderBy: string, orderType: string) => Promise<void>;
  onPaginationChange: (pageNumber: number, pageSize: number) => Promise<void>;
}

export const ProjectExportsTable = observer((props: IProps): ReactElement => {
  const { t } = useTranslator();
  const { items, paging, onOrderingChange, onPaginationChange } = props;

  const renderDropdown = (projectExport: IProjectExport): ReactElement => {
    if (projectExport.status === ProjectExportStatus.Finished) {
      return (
        <ProjectExportDropdownContainer
          projectExportId={projectExport.id}
          hasAllFilesGenerated={projectExport.hasAllFilesGenerated}
        />
      );
    }
    if (projectExport.status === ProjectExportStatus.InProgress) {
      return <S_TableClipLoader />;
    }
    return <></>;
  };

  const renderCreatedDate = (projectExport: IProjectExport): ReactElement => <>{toLocaleDateTimeString(projectExport.createdDate)}</>;

  const renderLastDatasetsModifiedDate = (projectExport: IProjectExport): ReactElement => <>{toLocaleDateTimeString(projectExport.lastDatasetsModifiedDate)}</>;

  const renderStatus = (projectExport: IProjectExport): ReactElement => {
    return <ExportStatusBadge status={projectExport.status} />;
  };

  const columns: IGColumnProps<IProjectExport>[] = [
    {
      sortBy: 'createdDate',
      headerName: t('new.projects.details.export_data.created_date'),
      renderer: renderCreatedDate,
    },
    {
      sortBy: 'fileSchemeVersion',
      headerName: t('new.projects.details.export_data.file_scheme_version'),
      renderer: (x) => x.fileSchemeVersion,
    },
    {
      sortBy: 'configurationVersion',
      headerName: t('new.projects.details.export_data.configuration_version'),
      renderer: (x) => x.configurationVersion,
    },
    {
      sortBy: 'annotationsVersion',
      headerName: t('new.projects.details.export_data.annotations_version'),
      renderer: (x) => x.annotationsVersion,
    },
    {
      sortBy: 'setVersion',
      headerName: t('new.projects.details.export_data.set_version'),
      renderer: (x) => x.setVersion,
    },
    {
      sortBy: 'lastDatasetsModifiedDate',
      headerName: t('new.projects.details.export_data.lastDatasetsModifiedDate'),
      renderer: renderLastDatasetsModifiedDate,
    },
    {
      sortBy: 'status',
      headerName: t('status'),
      renderer: renderStatus,
    },
    {
      headerName: t('new.projects.details.export_data.actions'),
      renderer: renderDropdown,
      actions: true,
      width: 'min-content',
    },
  ];

  return (
    <GTable<IProjectExport>
      isLoading={props.isLoading}
      columns={columns}
      items={items}
      noItemsPlaceholder={t('new.projects.details.export_data.no_exports_have_been_generated_yet')}
      paginationProps={{
        pageNumber: paging.pageNumber,
        pageSize: paging.pageSize,
        totalCount: paging.totalCount,
        onChange: onPaginationChange,
      }}
      onSortChanged={onOrderingChange}
      sortingModel={[
        {
          orderBy: paging.orderBy || '',
          orderType: paging.orderType || '',
        },
      ]}
    />
  );
});
